<template>
  <div class="dipsip-dashboard">
    <!-- Hero Section -->
    <div class="hero-section">
      <v-container class="py-8">
        <v-row class="text-center">
          <v-col cols="12">
            <div class="d-flex align-center justify-center mb-4">
              <v-icon color="white" size="48" class="mr-3"
                >mdi-chart-line</v-icon
              >
              <h1 class="text-h2 font-weight-bold text-white">DipSIP</h1>
            </div>
            <h2 class="text-h4 font-weight-medium mb-4 text-white">
              Build Core Portfolio around Low Cost Index Funds
            </h2>
            <p class="text-h6 text-grey-lighten-2 mb-2">
              Regret Buying on Highs, and missing the Dips? Try Dip SIP.
            </p>
            <p class="text-body-1 text-grey-lighten-1">
              A proven contra strategy for long term. Indexes are Darwinistic -
              Good Companies get added, Bad companies get kicked out.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Main Content -->
    <v-container class="py-6">
      <!-- Configuration Instructions -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-alert
            type="info"
            variant="outlined"
            prominent
            border="start"
            class="info-alert"
          >
            <template v-slot:prepend>
              <v-icon>mdi-information</v-icon>
            </template>
            <strong>How it works:</strong> Configure individual ETFs below. Set
            investment amount and trigger percentage for each ETF you want to
            subscribe to.
          </v-alert>
        </v-col>
      </v-row>

      <!-- ETF Configuration Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="modern-card">
            <!-- Card Header -->
            <v-card-title class="card-header pa-6">
              <div class="d-flex align-center">
                <v-icon color="white" size="28" class="mr-3">mdi-wallet</v-icon>
                <h2 class="text-h5 font-weight-bold text-white">
                  Configure Your Index Funds
                </h2>
              </div>
              <v-spacer />
              <v-btn
                variant="text"
                color="white"
                @click="selectionCriteriaDialog = true"
                prepend-icon="mdi-help-circle"
                class="text-white"
              >
                Selection Criteria
              </v-btn>
            </v-card-title>

            <!-- Search Section -->
            <v-card-text class="pa-6 search-section">
              <v-text-field
                v-model="searchText"
                label="Search ETFs by name or underlying asset"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-magnify"
                clearable
                hint="Type to filter ETFs - e.g., 'Nifty', 'Banking', etc."
                persistent-hint
                class="search-field"
              />
            </v-card-text>

            <!-- ETF List Section -->
            <v-card-text class="pa-0">
              <div class="etf-list-container">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    v-for="fund in filteredFundList"
                    :key="fund.instrument_token"
                  >
                    <v-card
                      class="etf-card ma-3"
                      elevation="2"
                      :class="{
                        'subscribed-card': getEtfConfig(fund.instrument_token)
                          .subscribed,
                        'unsubscribed-card': !getEtfConfig(
                          fund.instrument_token
                        ).subscribed,
                      }"
                    >
                      <v-card-text class="pa-4">
                        <v-row align="center" no-gutters>
                          <!-- ETF Information -->
                          <v-col cols="12" md="4" class="mb-3 mb-md-0">
                            <div
                              class="d-flex align-center justify-space-between"
                            >
                              <div class="etf-info">
                                <div class="d-flex align-center mb-2">
                                  <v-icon
                                    :color="
                                      getEtfConfig(fund.instrument_token)
                                        .subscribed
                                        ? 'primary'
                                        : 'grey'
                                    "
                                    class="mr-2"
                                  >
                                    mdi-chart-box
                                  </v-icon>
                                  <h3 class="text-h6 font-weight-bold">
                                    {{ fund.name }}
                                  </h3>
                                </div>
                                <p class="text-body-2 text-grey-darken-1 mb-2">
                                  {{ fund.displayName }}
                                </p>

                                <!-- Status Chips -->
                                <div class="status-chips">
                                  <v-chip
                                    v-if="
                                      getEtfStatus(fund.instrument_token) ===
                                      'saved'
                                    "
                                    color="success"
                                    size="small"
                                    variant="flat"
                                    class="status-chip"
                                  >
                                    <v-icon start size="12"
                                      >mdi-check-circle</v-icon
                                    >
                                    Subscribed
                                  </v-chip>
                                  <v-chip
                                    v-else-if="
                                      getEtfStatus(fund.instrument_token) ===
                                      'pending_subscribe'
                                    "
                                    color="orange"
                                    size="small"
                                    variant="flat"
                                    class="status-chip"
                                  >
                                    <v-icon start size="12">mdi-clock</v-icon>
                                    Will Subscribe
                                  </v-chip>
                                  <v-chip
                                    v-else-if="
                                      getEtfStatus(fund.instrument_token) ===
                                      'pending_unsubscribe'
                                    "
                                    color="error"
                                    size="small"
                                    variant="flat"
                                    class="status-chip"
                                  >
                                    <v-icon start size="12"
                                      >mdi-minus-circle</v-icon
                                    >
                                    Will Unsubscribe
                                  </v-chip>
                                  <v-chip
                                    v-else-if="
                                      getEtfStatus(fund.instrument_token) ===
                                      'pending_update'
                                    "
                                    color="info"
                                    size="small"
                                    variant="flat"
                                    class="status-chip"
                                  >
                                    <v-icon start size="12">mdi-pencil</v-icon>
                                    Will Update
                                  </v-chip>
                                </div>
                              </div>

                              <!-- Toggle Switch -->
                              <div class="toggle-section">
                                <v-switch
                                  :model-value="
                                    getEtfConfig(fund.instrument_token)
                                      .subscribed
                                  "
                                  @update:model-value="toggleSubscription(fund)"
                                  color="primary"
                                  density="comfortable"
                                  hide-details
                                />
                              </div>
                            </div>
                          </v-col>

                          <!-- Amount Input -->
                          <v-col cols="12" md="3" class="mb-3 mb-md-0 px-md-2">
                            <v-text-field
                              :model-value="
                                getEtfConfig(fund.instrument_token).amount
                              "
                              @update:model-value="
                                updateEtfAmount(fund.instrument_token, $event)
                              "
                              label="Investment Amount (₹)"
                              type="number"
                              variant="outlined"
                              density="comfortable"
                              hide-details
                              :disabled="
                                !getEtfConfig(fund.instrument_token).subscribed
                              "
                              prepend-inner-icon="mdi-currency-inr"
                              min="100"
                              step="100"
                              class="amount-field"
                            />
                          </v-col>

                          <!-- Trigger Input -->
                          <v-col cols="12" md="3" class="mb-3 mb-md-0 px-md-2">
                            <v-text-field
                              :model-value="
                                getEtfConfig(fund.instrument_token).trigger
                              "
                              @update:model-value="
                                updateEtfTrigger(fund.instrument_token, $event)
                              "
                              label="Trigger Percentage (%)"
                              type="number"
                              variant="outlined"
                              density="comfortable"
                              hide-details
                              :disabled="
                                !getEtfConfig(fund.instrument_token).subscribed
                              "
                              prepend-inner-icon="mdi-percent"
                              step="0.5"
                              min="0.5"
                              max="10"
                              class="trigger-field"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>

            <!-- Action Buttons -->
            <v-card-actions class="pa-6 action-section">
              <v-btn
                :disabled="!hasChanges"
                @click="saveAllConfigurations"
                color="primary"
                size="large"
                prepend-icon="mdi-content-save"
                variant="elevated"
                class="action-btn"
              >
                Save All Configurations
              </v-btn>

              <v-btn
                :disabled="!hasAnySubscriptions"
                @click="unsubscribeConfirmDialog = true"
                color="error"
                size="large"
                prepend-icon="mdi-cancel"
                variant="outlined"
                class="action-btn ml-4"
              >
                Unsubscribe All
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Selection Criteria Dialog -->
    <v-dialog v-model="selectionCriteriaDialog" max-width="600">
      <v-card class="criteria-dialog">
        <v-card-title class="dialog-header pa-6">
          <v-icon color="primary" class="mr-3">mdi-help-circle</v-icon>
          <span class="text-h5 font-weight-bold">ETF Selection Criteria</span>
        </v-card-title>

        <v-card-text class="pa-6">
          <div class="criteria-content">
            <h3 class="text-h6 font-weight-bold mb-3">
              How we select Index Funds:
            </h3>
            <v-list class="criteria-list">
              <v-list-item
                prepend-icon="mdi-check-circle"
                class="criteria-item"
              >
                <v-list-item-title
                  >Low expense ratio (< 0.5%)</v-list-item-title
                >
              </v-list-item>
              <v-list-item
                prepend-icon="mdi-check-circle"
                class="criteria-item"
              >
                <v-list-item-title
                  >High liquidity and trading volume</v-list-item-title
                >
              </v-list-item>
              <v-list-item
                prepend-icon="mdi-check-circle"
                class="criteria-item"
              >
                <v-list-item-title
                  >Track established indices (Nifty, Sensex,
                  etc.)</v-list-item-title
                >
              </v-list-item>
              <v-list-item
                prepend-icon="mdi-check-circle"
                class="criteria-item"
              >
                <v-list-item-title>Minimum tracking error</v-list-item-title>
              </v-list-item>
              <v-list-item
                prepend-icon="mdi-check-circle"
                class="criteria-item"
              >
                <v-list-item-title
                  >Reputable fund house management</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            color="primary"
            variant="elevated"
            @click="selectionCriteriaDialog = false"
          >
            Got it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unsubscribe Confirmation Dialog -->
    <v-dialog v-model="unsubscribeConfirmDialog" max-width="500">
      <v-card class="confirmation-dialog">
        <v-card-title class="dialog-header pa-6">
          <v-icon color="error" class="mr-3">mdi-alert</v-icon>
          <span class="text-h5 font-weight-bold">Confirm Unsubscribe</span>
        </v-card-title>

        <v-card-text class="pa-6">
          <div class="confirmation-content">
            <h3 class="text-h6 font-weight-bold mb-3 text-center">
              Are you sure you want to unsubscribe from all ETFs?
            </h3>
            <p class="text-body-1 text-grey-darken-1 text-center mb-4">
              This action will remove all your current ETF subscriptions and
              cannot be undone.
            </p>
            <v-alert
              type="warning"
              variant="tonal"
              prominent
              border="start"
              class="mb-0"
            >
              <template v-slot:prepend>
                <v-icon>mdi-information</v-icon>
              </template>
              <strong>Note:</strong> You will need to reconfigure your ETF
              subscriptions manually if you change your mind.
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6 justify-center">
          <v-btn
            color="grey"
            variant="outlined"
            @click="unsubscribeConfirmDialog = false"
            class="mr-4"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="confirmUnsubscribeAll"
            prepend-icon="mdi-check"
          >
            Yes, Unsubscribe All
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top"
      class="notification-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from "vuex";
import api from "./api";
import {
  GET_USER_ID_FROM_EMAIL,
  INSERT_USER_INSTRUMENT,
  UPSERT_USER_INSTRUMENT,
  DELETE_USER_INSTRUMENT,
  GET_USER_INSTRUMENTS,
} from "../lib/helper/queries";

export default {
  computed: {
    ...mapState(["loggedInGoogle", "userGoogle"]),
    filteredFundList() {
      if (!this.searchText) return this.fundList;
      const searchText = this.searchText.toLowerCase();
      const searchWords = searchText.split(/\s+/);
      return this.fundList.filter((fund) => {
        const itemText = (fund.name + " " + fund.displayName).toLowerCase();
        return searchWords.every((word) => itemText.includes(word));
      });
    },
    hasSubscribedFunds() {
      return Object.values(this.etfConfigs).some((config) => config.subscribed);
    },
    hasAnySubscriptions() {
      // Check if there are any current subscriptions OR any saved subscriptions
      const hasCurrentSubscriptions = Object.values(this.etfConfigs).some(
        (config) => config.subscribed
      );
      const hasSavedSubscriptions = Object.values(this.savedEtfConfigs).some(
        (config) => config.subscribed
      );
      return hasCurrentSubscriptions || hasSavedSubscriptions;
    },
    hasChanges() {
      return this.hasPendingChanges;
    },
    hasPendingChanges() {
      // Check if there are any differences between current config and saved config
      const currentTokens = new Set(
        Object.entries(this.etfConfigs)
          .filter(([_, config]) => config.subscribed)
          .map(([token, _]) => parseInt(token))
      );

      const savedTokens = new Set(
        Object.entries(this.savedEtfConfigs)
          .filter(([_, config]) => config.subscribed)
          .map(([token, _]) => parseInt(token))
      );

      // Check for additions or deletions
      if (currentTokens.size !== savedTokens.size) return true;

      for (let token of currentTokens) {
        if (!savedTokens.has(token)) return true;
      }

      // Check for updates in amount or trigger
      for (let [token, currentConfig] of Object.entries(this.etfConfigs)) {
        if (currentConfig.subscribed) {
          const savedConfig = this.savedEtfConfigs[token];
          if (
            savedConfig &&
            (savedConfig.amount !== currentConfig.amount ||
              savedConfig.trigger !== currentConfig.trigger)
          ) {
            return true;
          }
        }
      }

      return false;
    },
    subscribedFunds() {
      return this.fundList.filter(
        (fund) => this.etfConfigs[fund.instrument_token]?.subscribed
      );
    },
  },
  watch: {
    async userGoogle(newVal) {
      if (newVal && newVal.email) {
        if (this.fundList.length === 0) await this.fetchFundList();
        await this.loadUserConfigurations();
      }
    },
  },
  async mounted() {
    if (this.fundList.length === 0) await this.fetchFundList();
    if (this.userGoogle && this.userGoogle.email) {
      await this.loadUserConfigurations();
    }
  },
  data() {
    return {
      fundList: [],
      etfConfigs: {}, // Object to store individual ETF configurations
      savedEtfConfigs: {}, // Object to store saved configurations from database
      searchText: "",
      snackbar: {
        show: false,
        message: "",
        timeout: 3000,
        color: "success",
        icon: "mdi-check-circle",
      },
      selectionCriteriaDialog: false,
      unsubscribeConfirmDialog: false,
    };
  },
  methods: {
    // Get ETF configuration for a specific instrument token
    getEtfConfig(instrumentToken) {
      return (
        this.etfConfigs[instrumentToken] || {
          subscribed: false,
          amount: 1000,
          trigger: 2,
        }
      );
    },

    // Get ETF status to show appropriate status chips
    getEtfStatus(instrumentToken) {
      const currentConfig = this.getEtfConfig(instrumentToken);
      const savedConfig = this.savedEtfConfigs[instrumentToken];

      // If no saved config exists
      if (!savedConfig) {
        return currentConfig.subscribed ? "pending_subscribe" : "unsubscribed";
      }

      // If saved config exists
      if (savedConfig.subscribed && currentConfig.subscribed) {
        // Check if any values changed
        if (
          savedConfig.amount !== currentConfig.amount ||
          savedConfig.trigger !== currentConfig.trigger
        ) {
          return "pending_update";
        }
        return "saved";
      }

      if (savedConfig.subscribed && !currentConfig.subscribed) {
        return "pending_unsubscribe";
      }

      if (!savedConfig.subscribed && currentConfig.subscribed) {
        return "pending_subscribe";
      }

      return "unsubscribed";
    },

    // Initialize ETF configurations when fund list is loaded
    initializeEtfConfigs() {
      this.fundList.forEach((fund) => {
        if (!this.etfConfigs[fund.instrument_token]) {
          this.etfConfigs[fund.instrument_token] = {
            subscribed: false,
            amount: 1000,
            trigger: 2,
          };
        }
      });
    },

    // Toggle subscription for an ETF (local state only)
    toggleSubscription(fund) {
      const config = this.getEtfConfig(fund.instrument_token);
      this.etfConfigs[fund.instrument_token] = {
        ...config,
        subscribed: !config.subscribed,
      };
    },

    // Update ETF amount
    updateEtfAmount(instrumentToken, amount) {
      const config = this.getEtfConfig(instrumentToken);
      this.etfConfigs[instrumentToken] = {
        ...config,
        amount: parseInt(amount) || 1000,
      };
    },

    // Update ETF trigger
    updateEtfTrigger(instrumentToken, trigger) {
      const config = this.getEtfConfig(instrumentToken);
      this.etfConfigs[instrumentToken] = {
        ...config,
        trigger: parseFloat(trigger) || 2,
      };
    },

    async saveAllConfigurations() {
      const userId = await this.getUserIdByEmail(this.userGoogle.email);
      if (!userId) return;

      try {
        // Step 1: Get current database state
        const dbResult = await this.$apollo.query({
          query: GET_USER_INSTRUMENTS,
          variables: { user_id: userId },
          fetchPolicy: "no-cache",
        });

        const dbInstruments = dbResult.data.user_instrument;
        const dbTokens = new Set(
          dbInstruments.map((inst) => inst.instrument_token)
        );

        // Step 2: Get current UI configuration state
        const currentSubscriptions = Object.entries(this.etfConfigs)
          .filter(([_, config]) => config.subscribed)
          .map(([instrumentToken, config]) => {
            const fund = this.fundList.find(
              (f) => f.instrument_token == instrumentToken
            );
            return {
              instrument_token: parseInt(instrumentToken),
              user_id: userId,
              instrument_symbol: fund.name,
              notification_frequency: 1,
              value: config.amount,
              allocation_factor: 1,
              base_amount: config.amount,
              trigger: config.trigger,
              notification_frequency_unit: "1_day",
            };
          });

        const currentTokens = new Set(
          currentSubscriptions.map((sub) => sub.instrument_token)
        );

        // Step 3: Determine operations needed
        const toDelete = Array.from(dbTokens).filter(
          (token) => !currentTokens.has(token)
        );
        const toAdd = currentSubscriptions.filter(
          (sub) => !dbTokens.has(sub.instrument_token)
        );
        const toUpdate = currentSubscriptions.filter((sub) =>
          dbTokens.has(sub.instrument_token)
        );

        console.log("Sync operations:", {
          toDelete: toDelete.length,
          toAdd: toAdd.length,
          toUpdate: toUpdate.length,
        });

        // Step 4: Execute operations
        let operationsCount = 0;

        // Delete instruments that are in DB but not in current config
        if (toDelete.length > 0) {
          await this.$apollo.mutate({
            mutation: DELETE_USER_INSTRUMENT,
            variables: { user_id: userId, instrumentTokens: toDelete },
            fetchPolicy: "no-cache",
          });
          operationsCount += toDelete.length;
        }

        // Add/Update instruments that are in current config
        if (toAdd.length > 0 || toUpdate.length > 0) {
          const toUpsert = [...toAdd, ...toUpdate];
          await this.$apollo.mutate({
            mutation: UPSERT_USER_INSTRUMENT,
            variables: { objects: toUpsert },
            fetchPolicy: "no-cache",
          });
          operationsCount += toUpsert.length;
        }

        // Step 5: Update saved configurations and show success message
        if (operationsCount > 0) {
          // Update savedEtfConfigs to match current etfConfigs
          this.savedEtfConfigs = {};
          Object.entries(this.etfConfigs).forEach(([token, config]) => {
            if (config.subscribed) {
              this.savedEtfConfigs[token] = { ...config };
            }
          });

          this.showNotification(
            `Successfully synced ${operationsCount} ETF configurations (${toAdd.length} added, ${toUpdate.length} updated, ${toDelete.length} removed)`,
            "success",
            "mdi-check-circle"
          );
        } else {
          this.showNotification(
            "No changes detected - all configurations are up to date",
            "info",
            "mdi-information"
          );
        }
      } catch (e) {
        console.error("Sync failed:", e);
        this.showNotification(
          "Failed to sync configurations. Please try again.",
          "error",
          "mdi-alert-circle"
        );
      }
    },

    async confirmUnsubscribeAll() {
      // Close the confirmation dialog
      this.unsubscribeConfirmDialog = false;
      // Call the actual unsubscribe method
      await this.unsubscribeAll();
    },

    async unsubscribeAll() {
      const userId = await this.getUserIdByEmail(this.userGoogle.email);
      if (!userId) return;

      // Get all tokens that are either currently subscribed OR saved as subscribed
      const currentSubscribedTokens = Object.entries(this.etfConfigs)
        .filter(([_, config]) => config.subscribed)
        .map(([instrumentToken, _]) => parseInt(instrumentToken));

      const savedSubscribedTokens = Object.entries(this.savedEtfConfigs)
        .filter(([_, config]) => config.subscribed)
        .map(([instrumentToken, _]) => parseInt(instrumentToken));

      // Combine and deduplicate all subscribed tokens
      const allSubscribedTokens = [
        ...new Set([...currentSubscribedTokens, ...savedSubscribedTokens]),
      ];

      if (allSubscribedTokens.length === 0) {
        this.showNotification(
          "No subscribed ETFs to unsubscribe",
          "warning",
          "mdi-information"
        );
        return;
      }

      try {
        await this.$apollo.mutate({
          mutation: DELETE_USER_INSTRUMENT,
          variables: { user_id: userId, instrumentTokens: allSubscribedTokens },
          fetchPolicy: "no-cache",
        });

        // Update local state - unsubscribe all tokens
        allSubscribedTokens.forEach((token) => {
          if (this.etfConfigs[token]) {
            this.etfConfigs[token].subscribed = false;
          }
          // Also clear from saved configs
          if (this.savedEtfConfigs[token]) {
            delete this.savedEtfConfigs[token];
          }
        });

        this.showNotification(
          `Unsubscribed from ${allSubscribedTokens.length} ETFs`,
          "success",
          "mdi-check-circle"
        );
      } catch (e) {
        console.error("Error unsubscribing:", e);
        this.showNotification(
          "Unsubscribe failed. Try again.",
          "error",
          "mdi-alert-circle"
        );
      }
    },

    // Legacy methods for backward compatibility
    async saveConfig() {
      await this.saveAllConfigurations();
    },

    async deleteConfig() {
      await this.unsubscribeAll();
    },
    async getUserIdByEmail(email) {
      try {
        const result = await this.$apollo.query({
          query: GET_USER_ID_FROM_EMAIL,
          variables: { email },
          fetchPolicy: "no-cache",
        });
        const user = result.data.users[0];
        return user?.id || null;
      } catch (e) {
        console.error("Error fetching user ID:", e);
        return null;
      }
    },
    showNotification(message, color = "success", icon = "mdi-check-circle") {
      this.snackbar = {
        show: true,
        message,
        color,
        icon,
        timeout: 3000,
      };
    },

    async saveConfigKeyVal() {
      // This method is kept for backward compatibility but now calls saveAllConfigurations
      await this.saveAllConfigurations();
    },

    async fetchFundList() {
      try {
        const response = await api.get("/api/nse/etfList");
        this.fundList = response.data.map((m) => ({
          name: m.symbol,
          displayName: m.underlying,
          instrument_token: m.instrument_token,
          subscribing: false, // Add loading state for individual subscribe
          unsubscribing: false, // Add loading state for individual unsubscribe
        }));

        // Initialize ETF configurations after loading fund list
        this.initializeEtfConfigs();
      } catch (error) {
        console.error("Error fetching fund list:", error);
      }
    },

    async loadUserConfigurations() {
      if (!this.userGoogle || !this.userGoogle.email) return;

      try {
        const userId = await this.getUserIdByEmail(this.userGoogle.email);
        if (!userId) return;

        const result = await this.$apollo.query({
          query: GET_USER_INSTRUMENTS,
          variables: { user_id: userId },
          fetchPolicy: "no-cache",
        });

        const userInstruments = result.data.user_instrument;

        // Update etfConfigs with existing user configurations
        userInstruments.forEach((instrument) => {
          const config = {
            subscribed: true,
            amount: instrument.base_amount || instrument.value || 1000,
            trigger: instrument.trigger || 2,
          };

          // Set both current and saved configs
          this.etfConfigs[instrument.instrument_token] = { ...config };
          this.savedEtfConfigs[instrument.instrument_token] = { ...config };
        });

        console.log("Loaded user configurations:", userInstruments);
      } catch (e) {
        console.error("Error loading user configurations:", e);
      }
    },

    handleSearch(val) {
      this.searchText = val;
    },

    // Keep this method for potential future use
    customFilter(item, queryText) {
      const itemText = item.toLowerCase();
      const searchText = queryText.toLowerCase();
      const searchWords = searchText.split(/\s+/);
      return searchWords.every((word) => itemText.includes(word));
    },
  },
};
</script>

<style scoped>
/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  background-attachment: fixed;
  color: white;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.9) 0%,
    rgba(29, 78, 216, 0.9) 100%
  );
  z-index: 1;
}

.hero-section .v-container {
  position: relative;
  z-index: 2;
}

/* Main Dashboard */
.dipsip-dashboard {
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

/* Info Alert */
.info-alert {
  border-left: 4px solid #3b82f6 !important;
  background: linear-gradient(135deg, #ebf8ff 0%, #dbeafe 100%) !important;
  border-radius: 8px !important;
}

/* Modern Card */
.modern-card {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
}

/* Card Header */
.card-header {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
}

/* Search Section */
.search-section {
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0;
}

.search-field {
  max-width: 500px;
  margin: 0 auto;
}

/* ETF Cards */
.etf-list-container {
  max-height: 600px;
  overflow-y: auto;
}

.etf-card {
  border-radius: 8px !important;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
}

.etf-card:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.subscribed-card {
  border-left: 4px solid #10b981 !important;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%) !important;
}

.unsubscribed-card {
  border-left: 4px solid #94a3b8 !important;
  background: #ffffff !important;
}

/* ETF Info */
.etf-info h3 {
  color: #1e293b;
  line-height: 1.3;
}

/* Status Chips */
.status-chips {
  margin-top: 8px;
}

.status-chip {
  font-weight: 500 !important;
  border-radius: 6px !important;
}

/* Toggle Section */
.toggle-section {
  min-width: 60px;
  display: flex;
  justify-content: flex-end;
}

/* Form Fields */
.amount-field,
.trigger-field {
  border-radius: 8px;
}

.amount-field :deep(.v-field__input) {
  font-weight: 500;
}

.trigger-field :deep(.v-field__input) {
  font-weight: 500;
}

/* Action Section */
.action-section {
  background: #f8fafc !important;
  border-top: 1px solid #e2e8f0;
  justify-content: center;
}

.action-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Dialog Styles */
.criteria-dialog {
  border-radius: 12px !important;
}

.confirmation-dialog {
  border-radius: 12px !important;
}

.dialog-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  border-bottom: 1px solid #e2e8f0;
}

.criteria-content {
  background: #ffffff;
}

.confirmation-content {
  background: #ffffff;
}

.confirmation-content h3 {
  color: #1e293b;
}

.criteria-list {
  background: transparent !important;
}

.criteria-item {
  margin-bottom: 8px;
  padding: 8px 0;
}

.criteria-item :deep(.v-list-item__prepend .v-icon) {
  color: #10b981 !important;
}

/* Notification Snackbar */
.notification-snackbar {
  border-radius: 8px !important;
}

.notification-snackbar :deep(.v-snackbar__content) {
  font-weight: 500;
}

/* Custom Scrollbar */
.etf-list-container::-webkit-scrollbar {
  width: 6px;
}

.etf-list-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.etf-list-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.etf-list-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive Design */
@media (max-width: 960px) {
  .hero-section {
    background-attachment: scroll;
  }

  .etf-card {
    margin: 8px !important;
  }

  .action-section {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn {
    margin: 4px 0 !important;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .hero-section .text-h2 {
    font-size: 2rem !important;
  }

  .hero-section .text-h4 {
    font-size: 1.5rem !important;
  }

  .etf-list-container {
    max-height: 500px;
  }
}
</style>
